// Fluid zone schema
// Represents a region with specific fluid properties

import { z } from 'zod';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Fluid Zone Schema
 * Represents a region with specific fluid properties used in calculations
 * 
 * Fluid zones are normalized at the document level and referenced by ID
 * from edges that contain fluid (ADR-0003: Graph-Based Structure with ID References)
 */
export const FluidZoneSchema = z.object({
  id: z.string(),
  name: z.string(),

  // Calculation data
  fluid_name: z.string(),
  temperature: ValueWithUnitSchema,
  density: ValueWithUnitSchema,
  viscosity: ValueWithUnitSchema,

  /**
   * Phase of the zone's fluid as emitted by the desktop converter
   * ('liquid' | 'vapor'). Kept as a plain string so an unexpected value
   * fails at the consumer (with the consuming device named) rather than
   * failing the whole document parse. Ducts require 'vapor'
   * (check_duct_requirements.cpp W_LIQUID_IN_DUCT).
   */
  fluid_state: z.string().optional(),

  // v2 additions — extended fluid model
  note: z.string().optional(),
  specific_heat: ValueWithUnitSchema.optional(),
  vapor_pressure: ValueWithUnitSchema.optional(),
  bulk_modulus: ValueWithUnitSchema.optional(),
  surface_tension: ValueWithUnitSchema.optional(),
  /** Ratio of specific heats Cp/Cv (for compressible fluids) */
  specific_heat_ratio: z.number().optional(),
  molecular_weight: z.number().optional(),
  /**
   * Compressibility factor Z for real-gas behavior. Precedence ladder
   * for the compressible solver (highest to lowest priority):
   *
   *   1. `compressibility_factor` (scalar) — Slice 2h explicit override.
   *   2. `components[]` — Slice 2X mixture path. Each component is
   *      resolved against the fluid database; the bridge calls the
   *      Peng-Robinson mixture EOS at each pipe's local pressure.
   *   3. `critical_temperature` + `critical_pressure` + `acentric_factor`
   *      — Slice 2T/2V pure-gas Peng-Robinson path, evaluated per pipe.
   *   4. Otherwise → ideal gas (Z = 1).
   */
  compressibility_factor: z.number().optional(),
  /** Critical temperature Tc (K), required for Peng-Robinson Z(T,P). */
  critical_temperature: ValueWithUnitSchema.optional(),
  /** Critical pressure Pc (Pa), required for Peng-Robinson Z(T,P). */
  critical_pressure: ValueWithUnitSchema.optional(),
  /** Acentric factor ω (dimensionless), required for Peng-Robinson Z(T,P). */
  acentric_factor: z.number().optional(),
  /**
   * Slice 2X: gas-mixture composition. When present, the compressible
   * solver dispatches to the Peng-Robinson mixture path at bridge
   * time. Each component references a fluid in the fluid database by
   * `fluid_id`; the database supplies Tc, Pc, ω, MW, k per component.
   * Mole fractions must sum to 1 (within 1e-4 tolerance).
   */
  components: z
    .array(
      z.object({
        fluid_id: z.string(),
        mole_fraction: z.number().min(0).max(1),
      }),
    )
    .optional()
    .refine(
      (list) => {
        if (!list) return true;
        const sum = list.reduce((s, c) => s + c.mole_fraction, 0);
        return Math.abs(sum - 1) < 1e-4;
      },
      { message: 'components mole fractions must sum to 1 (within 1e-4)' },
    ),
  /** Reference pressure for gas calculations (Pa). Used by compressor for PR-to-dP conversion. */
  pressure: ValueWithUnitSchema.optional(),

  // Fluid database integration
  /** ID of the fluid in the static database (e.g. "water", "ammonia") */
  fluid_id: z.string().optional(),
  /** How properties were obtained: database, mixture, or custom (manual entry) */
  fluid_source: z.enum(['database', 'mixture', 'custom']).optional(),
  /** ID of a pre-defined mixture (e.g. "R-410A", "AC9100") */
  mixture_id: z.string().optional(),
  /** Concentration ratio for glycol/water blends (0.0–1.0) */
  mixture_ratio: z.number().min(0).max(1).optional(),
});

export type FluidZone = z.infer<typeof FluidZoneSchema>;

